import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { Row, Col, Card, Form, Button, Alert } from 'react-bootstrap';
import { NavLink } from 'react-router-dom';
import { IoIosArrowBack } from 'react-icons/io';
import { TiDelete } from 'react-icons/ti';
import { IoMdAddCircle } from 'react-icons/io';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';

import ScanPointSettingsForm from './ScanPointSettingsForm/ScanPointSettingsForm';
import BillingDetailsForm from './BillingDetailsForm/BillingDetailsForm';
import HelpBlock from '../../Layout/HelpBlock/HelpBlock';
import LoadingOverLay from '../../common/LoadingOverlay';
import SupportModal from '../../Modals/SupportModal/SupportModal';

import api from '../../../api/index';
import { toastrAdd } from '../../../actions/index';

import './ScanPointOrder.scss';

function ScanPointOrder(props) {
    const partnerID = localStorage.getItem("partnerID");
    const { eventID } = props;
    const [partnerLoading, setPartnerLoading] = useState(null);
    const [partner, setPartner] = useState({});
    const [orderRows, setOrderRows] = useState([]);
    const [modalShow, setModalShow] = useState(false);
    const totalPrice = (orderRows.length > 0) ? orderRows.map(r => r.price).reduce((prev, next) => prev + next) : 0;
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(false);
    const [orderSuccess, setOrderSuccess] = useState(false);
    const [orderedScanPoints, setOrderedScanPoints] = useState([]);
    const emptyScanPoint = {
        name: '',
        description: '',
        qrEnabled: true,
        commentsEnabled: true,
        partnerID,
        salesType: 'partner'
    };

    const initialBillingDetails = {
        // contactPerson: '',
        invoiceCompany: partner.invoiceCompany || '',
        invoiceCompanyBusinessID: partner.invoiceCompanyBusinessID || '',
        eInvoiceOperator: partner.eInvoiceOperator || '',
        eInvoiceIntermediateId: partner.eInvoiceIntermediateId || '',
        eInvoiceAddress: partner.eInvoiceAddress || '',
        invoiceReference: partner.invoiceReference || '',
        invoicePostalCode: partner.invoicePostalCode || '',
        invoiceCity: partner.invoiceCity || '',
        invoiceStreetAddress: partner.invoiceStreetAddress || '',
        // additionalDetails: '',
    };

    const _onSuccess = (response) => {
        dispatch(toastrAdd({ code: 0, details: t('scan_points.scan_points_order_success_details'), title: t('scan_points.scan_points_order_success_title'), type: 'success' }));
        setLoading(false);
        setOrderSuccess(true);
        setOrderedScanPoints(response.data.data.scanPoints);
    };

    const _onError = (error) => {
        const { firstError } = error;
        const errorDetails = firstError ? `${firstError.title} - ${firstError.details}` : '';
        setLoading(false);

        dispatch(toastrAdd({
            code: 0,
            title: t('scan_points.scan_points_order_fail_title'),
            details: `${t('general.details')}: ${errorDetails}`,
            type: 'error'
        }));
    };

    const _orderScanPoints = (scanPoints) => {
        api.scanpoint.orderScanPoints(eventID, scanPoints)
            .then(_onSuccess)
            .catch(_onError);
    };

    const _updatePartner = ({ billingDetails, scanPoints }) => {
        api.partner.savePartner(partnerID, { ...partner, ...billingDetails })
            .then(() => _orderScanPoints(scanPoints))
            .catch(_onError);
    };

    const _setToInitial = () => {
        setOrderSuccess(false);
        setOrderRows([{
            name: t('scan_points.eventos_contact_scan_point'),
            amount: 1,
            price: 190.00
        }]);
        formik.setValues({...values, scanPoints: [emptyScanPoint]}, false);
    };

    /**
     * Formik configuration
     */
    const formik = useFormik({
        initialValues: {
            scanPoints: [{...emptyScanPoint}],
            billingDetails: initialBillingDetails
        },
        // Validation schema
        validationSchema: Yup.object().shape({
            scanPoints: Yup.array().of(Yup.object().shape({
                name: Yup.string().required(t('form.name_required')),
            })),
            billingDetails: Yup.object().shape({
                invoiceCompany: Yup.string().required('form.required'),
                invoiceCompanyBusinessID: Yup.string().required('form.required')
            })
        }),
        enableReinitialize: true,
        onSubmit: values => {
            const { scanPoints, billingDetails } = values;
            const partnerDetailsChanged = JSON.stringify(initialBillingDetails) !== JSON.stringify(billingDetails);
            /**
             * if partner (billing) details have changed, update partner and then make scan points order sequentially
             */
            setLoading(true);

            if (partnerDetailsChanged) {
                _updatePartner(values);
            } else {
                _orderScanPoints(scanPoints);
            }
        },
    });

    const { errors, touched, values, setValues } = formik;

    /**
     * Handle Scan point item removal.
     *
     * @param {int} index
     */
    const _handleScanPointRemove = index => {
        let updatedValues = values.scanPoints.filter((scanPoint, scanPointIndex) => scanPointIndex !== index);
        formik.setValues({...values, scanPoints: [...updatedValues]})
    }


    /**
     * Handle scan point item add.
     */
    const _handleScanPointAdd = () => {
        formik.setValues({...values, scanPoints: [...values.scanPoints, { ...emptyScanPoint, salesType: 'partnerAdditional' }]})
    }

    /**
     * Fetch Partner
     */
    const _fetchPartner = useCallback((eventID, partnerID) => {
        setPartnerLoading(true);

        api.partner.getEventPartner(eventID, partnerID).then(response => {
            setPartner(response.data.data.partners);
        }).finally(() => setPartnerLoading(false))
    }, []);

    useEffect(() => {
        if (partnerID) {
           _fetchPartner(eventID, partnerID)
        }
    }, [eventID, _fetchPartner, partnerID]);

    useEffect(() => {
        let newOrderRows = [{
            name: t('scan_points.eventos_contact_scan_point'),
            amount: 1,
            price: 190.00
        }]
        if(values.scanPoints.length > 1) {
            let extraScanPointAmount = values.scanPoints.length - 1;
            newOrderRows.push({
                name: t('scan_points.eventos_contact_scan_point_additional'),
                amount: extraScanPointAmount,
                price: extraScanPointAmount * 99.00
            });
        }
        setOrderRows(newOrderRows);
    }, [values, t]);

    return (
        <React.Fragment>

            <div className="row" style={{ marginBottom: '30px'}}>
                <div className="col">
                    <NavLink to={`../scanPoints`} style={{position: 'relative', background: 'white', padding: '6px 15px', borderRadius: '2px', marginBottom: '20px'}}>
                        <IoIosArrowBack /> {t('scan_points.back_to_scan_points')}
                    </NavLink>
                </div>
            </div>

            <h3>{t('scan_points.order_new_scan_point')}</h3>

            <Form onSubmit={formik.handleSubmit}>
                <Row>
                    <Col lg={8}>

                        {!orderSuccess ?
                            <>
                                <Card>
                                    <Card.Header>
                                        {t('scan_points.order_scan_point')}
                                    </Card.Header>
                                    <Card.Body style={{ position: 'relative' }}>
                                        {loading ?
                                            <LoadingOverLay />
                                        : null}
                                        <HelpBlock>
                                            <span>{t('scan_points.order_scan_point_help')}</span>
                                        </HelpBlock>

                                        <div className="ScanPointFieldsets">
                                            {values.scanPoints.map((scanPoint, index) => (
                                                <fieldset className="ScanPointFieldset" key={index}>
                                                    {index > 0 &&
                                                        <span
                                                            onClick={() => _handleScanPointRemove(index)}
                                                            className="ScanPointFieldsetRemove"
                                                            style={{ fontSize: '13px'}}
                                                        >
                                                            {t('scan_points.remove_scan_point_from_order')} <TiDelete />
                                                        </span>
                                                    }
                                                    <ScanPointSettingsForm
                                                        {...props}
                                                        index={index}
                                                        values={scanPoint}
                                                        touched={touched.scanPoints}
                                                        errors={errors.scanPoints}
                                                        setValues={setValues}
                                                        handleInputChange={formik.handleChange}
                                                    />
                                                </fieldset>
                                            ))}
                                        </div>
                                        <p className="text-muted">{t('scan_points.addiotional_scan_point_cost')} 99€</p>
                                        <Button
                                            type="button"
                                            size="sm"
                                            onClick={_handleScanPointAdd} variant="success"
                                            style={{ marginRight: '10px' }}
                                        >
                                            <IoMdAddCircle style={{ position: 'relative', top: '-2px', marginRight: '6px' }} />
                                            {t('scan_points.add_scan_point')}
                                        </Button>

                                    </Card.Body>
                                </Card>
                                <Card>
                                    <Card.Header>
                                        {t('general.billing_details')}
                                    </Card.Header>
                                    <Card.Body style={{ position: 'relative' }}>
                                        {loading ?
                                            <LoadingOverLay />
                                        : null}
                                        <BillingDetailsForm
                                            values={values.billingDetails}
                                            touched={touched.billingDetails}
                                            errors={errors.billingDetails}
                                            handleInputChange={formik.handleChange}
                                            partnerLoading={partnerLoading}
                                        />
                                    </Card.Body>
                                </Card>
                            </>
                        : null}

                        {orderSuccess ?
                            <Alert variant="success">
                                <strong>{t('scan_points.scan_points_order_success_details')}</strong>
                            </Alert>
                        : null}

                        <Card>
                            <Card.Header>{t('scan_points.order_summary')}</Card.Header>
                            <Card.Body style={{ position: 'relative' }}>

                                {loading ?
                                    <LoadingOverLay />
                                : null}

                                <table className="table table-striped" style={{fontSize: '13px'}}>
                                    <thead>
                                        <tr>
                                            <th>{t('scan_points.product')}</th>
                                            <th>{t('scan_points.amount')}</th>
                                            <th>{t('scan_points.price')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {orderRows.map((row, index) => (
                                            <tr key={index}>
                                                <td>{row.name}</td>
                                                <td>x {row.amount}</td>
                                                <td>{row.price} €</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    {orderRows.length > 1 ?
                                        <tfoot>
                                            <tr>
                                                <th>{t('orders.total')}</th>
                                                <td>{totalPrice} €</td>
                                            </tr>
                                        </tfoot>
                                    : null}
                                </table>
                                        
                                {orderSuccess ?
                                    <div style={{ paddingLeft: '.75rem', fontSize: '13px' }}>
                                        <p style={{ fontWeight: 'bold' }}>
                                            {t('scan_points.scan_points')}
                                        </p>
                                        {orderedScanPoints.map(row => (
                                            <p key={row.scanPointID}>
                                                {row.name}
                                            </p>
                                        ))}
                                    </div>
                                : null}
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col lg={4}>
                        <Card>
                            <Card.Header>{t('instructions.help')}</Card.Header>
                            <Card.Body>
                                <p style={{ fontWeight: '700'}}>{t('instructions.scan_points.order_scan_points_header')}</p>
                                <p>{t('instructions.scan_points.order_scan_points_text1')}</p>
                                <p>{t('instructions.scan_points.order_scan_points_text2')}</p>
                                <p style={{ fontWeight: '700'}}>{t('instructions.support_header')}</p>
                                <p>{t('instructions.support_text')}</p>
                                <Button type="button" variant="primary" size="sm"  onClick={() => setModalShow(true)}>{t('instructions.open_support_form')}</Button>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                {(Object.keys(errors).length !== 0 && Object.keys(touched).length !== 0) &&
                <p className="text-danger">
                    {t('scan_points.check_order_details')}
                </p>}

                {!orderSuccess ?
                    <Button type="submit" variant="success">
                        {t('scan_points.send_order')}
                    </Button>
                : null}

                {orderSuccess ?
                    <Button variant="success" onClick={_setToInitial}>
                        {t('Uusi tilaus')}
                    </Button>
                : null}

            </Form>

            <SupportModal
                show={modalShow}
                onHide={() => setModalShow(false)}
            />
        </React.Fragment>
    )
}

export default ScanPointOrder;